import React, { useMemo } from "react";
import { Form } from "react-final-form";

import { selectVersionConfig } from "fond/api";
import { Maybe, ScenarioConfiguration, VersionScenarioConfiguration } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

import ConfigurationStep from "./ConfigurationStep";
import { convertConfigToFormData } from "./helper";
import LayerSelectionStep from "./LayerSelectionStep";
import UploadConfigurationStep from "./UploadConfigurationStep";

export interface FormData extends ScenarioConfiguration {
  // Files uploaded by the user to the form, but not directly sent in the payload
  Files: {
    Occupancy: File | null;
    Artifacts: File[];
  };
}

const defaults: FormData = {
  Files: {
    Occupancy: null,
    Artifacts: [],
  },
  ToolConfigurations: {
    CostToServe: {
      CostingLayer: null,
      BinCount: 5,
      AdditionalSettings: `{ "root_node_filter": ["all", ["==", ["prop", ["elementData"], "Tier"], "T3"], ["==", ["prop", ["elementData"], "Type"], "HUB"]]}`,
    },
    DesignPhasePartitioner: {
      AdditionalSettings: `{ "root_node_filter": ["all", ["==", ["prop", ["elementData"], "Tier"], "T3"], ["==", ["prop", ["elementData"], "Type"], "HUB"]]}`,
    },
  },
  Inputs: {
    BillOfMaterials: {
      Type: "file",
      Data: null,
    },
    CostBreakdown: {
      Type: "file",
      Data: null,
    },
    Occupancy: {
      Type: "layer",
      Layer: { LayerKey: null },
    },
    ToolLayersConfiguration: {
      LayersConfiguration: {
        Type: "file",
        Data: "",
      },
      Mappings: [],
    },
  },
};

interface SetupFormProps {
  activeStep: number;
  configuration: Maybe<VersionScenarioConfiguration>;
  onSubmit: (values: FormData) => void;
  onNext: () => void;
}

// TODO - maybe change enum?
enum Steps {
  UPLOAD,
  LAYER_SELECTION,
  CONFIGURATION,
}

const SetupForm: React.FC<SetupFormProps> = ({ activeStep, configuration, onNext, onSubmit }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const config = useAppSelector((state) => selectVersionConfig(state, versionId));
  const initialValues = useMemo(() => {
    if (configuration) {
      return convertConfigToFormData(configuration);
    }
    return defaults;
  }, [configuration]);

  const handleOnSubmit = (values: FormData) => {
    if (activeStep < Steps.CONFIGURATION) {
      onNext();
    } else {
      onSubmit(values);
    }
  };

  const renderStepContent = (step: Steps) => {
    switch (step) {
      case Steps.UPLOAD:
        return <UploadConfigurationStep />;
      case Steps.LAYER_SELECTION:
        return <LayerSelectionStep config={config} />;
      case Steps.CONFIGURATION:
        return <ConfigurationStep config={config} />;
      default:
        return null;
    }
  };

  return (
    <Form<FormData>
      onSubmit={handleOnSubmit}
      initialValues={initialValues}
      render={({ handleSubmit }) => (
        <form id="collaboration-cts-form" onSubmit={handleSubmit}>
          {renderStepContent(activeStep)}
        </form>
      )}
    />
  );
};

export default SetupForm;
